import { Course } from './types';

export const REACT_TESTING_COURSE: Course = {
  id: "react-testing",
  title: "React Testing Mastery",
  description: "Master testing React applications with modern tools and best practices",
  level: "intermediate",
  duration: "10 hours",
  totalDuration: "10 hours",
  estimatedHours: 10,
  badge: "Testing Expert",
  modules: [
    {
      id: "testing-1",
      title: "Testing Fundamentals",
      description: "Learn the basics of testing React applications",
      duration: "90 minutes",
      estimatedTime: 90,
      difficulty: 'intermediate',
      prerequisites: ['advanced-8'],
      learningObjectives: [
        "Understand testing pyramid and strategies",
        "Set up testing environment",
        "Write your first React tests",
        "Use React Testing Library effectively"
      ],
      content: `
        <h1>Testing Fundamentals</h1>
        
        <h2>Why Test React Applications?</h2>
        <ul>
          <li><strong>Confidence:</strong> Ensure your app works as expected</li>
          <li><strong>Regression Prevention:</strong> Catch bugs before they reach users</li>
          <li><strong>Documentation:</strong> Tests serve as living documentation</li>
          <li><strong>Refactoring Safety:</strong> Change code with confidence</li>
        </ul>
        
        <h2>Testing Pyramid</h2>
        <pre><code>       /\\
      /E2E\\     ← Few, slow, expensive
     /____\\
    /      \\
   /Integration\\  ← Some, medium cost
  /__________\\
 /            \\
/   Unit Tests  \\   ← Many, fast, cheap
/______________\\</code></pre>
        
        <h2>Testing Tools Ecosystem</h2>
        <ul>
          <li><strong>Jest:</strong> Test runner and assertion library</li>
          <li><strong>React Testing Library:</strong> Testing utilities for React</li>
          <li><strong>MSW:</strong> Mock Service Worker for API mocking</li>
          <li><strong>Cypress/Playwright:</strong> End-to-end testing</li>
        </ul>
        
        <h2>Your First React Test</h2>
        <pre><code>import React from 'react';
import { render, screen } from '@testing-library/react';
import '@testing-library/jest-dom';
import Button from './Button';

test('renders button with text', () => {
  render(<Button>Click me</Button>);
  const buttonElement = screen.getByText(/click me/i);
  expect(buttonElement).toBeInTheDocument();
});

test('button is clickable', () => {
  const handleClick = jest.fn();
  render(<Button onClick={handleClick}>Click me</Button>);
  
  const button = screen.getByRole('button');
  button.click();
  
  expect(handleClick).toHaveBeenCalledTimes(1);
});</code></pre>
        
        <h2>React Testing Library Philosophy</h2>
        <blockquote>
          "The more your tests resemble the way your software is used, 
          the more confidence they can give you."
        </blockquote>
        
        <h3>Testing Library Principles:</h3>
        <ul>
          <li>Test behavior, not implementation</li>
          <li>Query by accessibility attributes</li>
          <li>Avoid testing internal state</li>
          <li>Focus on user interactions</li>
        </ul>
      `,
      practice: {
        task: "Write tests for a simple Counter component using React Testing Library",
        starter: `// Test this Counter component
const Counter = () => {
  const [count, setCount] = useState(0);
  
  return (
    <div>
      <h1>Count: {count}</h1>
      <button onClick={() => setCount(count + 1)}>Increment</button>
      <button onClick={() => setCount(count - 1)}>Decrement</button>
      <button onClick={() => setCount(0)}>Reset</button>
    </div>
  );
};`,
        solution: `import React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import '@testing-library/jest-dom';
import Counter from './Counter';

describe('Counter Component', () => {
  test('renders initial count of 0', () => {
    render(<Counter />);
    expect(screen.getByText('Count: 0')).toBeInTheDocument();
  });

  test('increments count when increment button is clicked', () => {
    render(<Counter />);
    const incrementButton = screen.getByText('Increment');
    
    fireEvent.click(incrementButton);
    expect(screen.getByText('Count: 1')).toBeInTheDocument();
    
    fireEvent.click(incrementButton);
    expect(screen.getByText('Count: 2')).toBeInTheDocument();
  });

  test('decrements count when decrement button is clicked', () => {
    render(<Counter />);
    const decrementButton = screen.getByText('Decrement');
    
    fireEvent.click(decrementButton);
    expect(screen.getByText('Count: -1')).toBeInTheDocument();
  });

  test('resets count to 0 when reset button is clicked', () => {
    render(<Counter />);
    const incrementButton = screen.getByText('Increment');
    const resetButton = screen.getByText('Reset');
    
    // Increment a few times
    fireEvent.click(incrementButton);
    fireEvent.click(incrementButton);
    expect(screen.getByText('Count: 2')).toBeInTheDocument();
    
    // Reset
    fireEvent.click(resetButton);
    expect(screen.getByText('Count: 0')).toBeInTheDocument();
  });
});`,
        hints: [
          "Use screen.getByText to find elements by their text content",
          "Use fireEvent.click to simulate button clicks",
          "Test the initial state and state changes"
        ]
      }
    },
    {
      id: "testing-2",
      title: "Testing User Interactions",
      description: "Master testing complex user interactions and form handling",
      duration: "100 minutes",
      estimatedTime: 100,
      difficulty: 'intermediate',
      prerequisites: ['testing-1'],
      learningObjectives: [
        "Test form inputs and validation",
        "Handle async user interactions",
        "Test keyboard and mouse events",
        "Use user-event for realistic interactions"
      ],
      content: `
        <h1>Testing User Interactions</h1>
        
        <h2>User Event vs FireEvent</h2>
        <p><strong>user-event</strong> provides more realistic user interactions than fireEvent:</p>
        
        <pre><code>import userEvent from '@testing-library/user-event';

// fireEvent - low level, immediate
fireEvent.click(button);
fireEvent.change(input, { target: { value: 'text' } });

// userEvent - high level, realistic
const user = userEvent.setup();
await user.click(button);
await user.type(input, 'text');</code></pre>
        
        <h2>Testing Forms</h2>
        <pre><code>import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import ContactForm from './ContactForm';

test('submits form with valid data', async () => {
  const user = userEvent.setup();
  const mockSubmit = jest.fn();
  
  render(<ContactForm onSubmit={mockSubmit} />);
  
  // Fill out form
  await user.type(screen.getByLabelText(/name/i), 'John Doe');
  await user.type(screen.getByLabelText(/email/i), 'john@example.com');
  await user.type(screen.getByLabelText(/message/i), 'Hello world');
  
  // Submit form
  await user.click(screen.getByRole('button', { name: /submit/i }));
  
  // Verify submission
  await waitFor(() => {
    expect(mockSubmit).toHaveBeenCalledWith({
      name: 'John Doe',
      email: 'john@example.com',
      message: 'Hello world'
    });
  });
});

test('shows validation errors for invalid data', async () => {
  const user = userEvent.setup();
  render(<ContactForm onSubmit={jest.fn()} />);
  
  // Submit without filling required fields
  await user.click(screen.getByRole('button', { name: /submit/i }));
  
  // Check for validation errors
  expect(screen.getByText(/name is required/i)).toBeInTheDocument();
  expect(screen.getByText(/email is required/i)).toBeInTheDocument();
});

test('validates email format', async () => {
  const user = userEvent.setup();
  render(<ContactForm onSubmit={jest.fn()} />);
  
  await user.type(screen.getByLabelText(/email/i), 'invalid-email');
  await user.click(screen.getByRole('button', { name: /submit/i }));
  
  expect(screen.getByText(/invalid email format/i)).toBeInTheDocument();
});</code></pre>
        
        <h2>Testing Keyboard Interactions</h2>
        <pre><code>test('navigates dropdown with keyboard', async () => {
  const user = userEvent.setup();
  render(<Dropdown options={['Apple', 'Banana', 'Cherry']} />);
  
  const trigger = screen.getByRole('button');
  await user.click(trigger);
  
  // Use arrow keys to navigate
  await user.keyboard('{ArrowDown}');
  expect(screen.getByText('Apple')).toHaveClass('highlighted');
  
  await user.keyboard('{ArrowDown}');
  expect(screen.getByText('Banana')).toHaveClass('highlighted');
  
  // Select with Enter
  await user.keyboard('{Enter}');
  expect(screen.getByText('Banana')).toBeInTheDocument();
  
  // Close with Escape
  await user.keyboard('{Escape}');
  expect(screen.queryByRole('listbox')).not.toBeInTheDocument();
});</code></pre>
        
        <h2>Testing Drag and Drop</h2>
        <pre><code>test('reorders items with drag and drop', async () => {
  const user = userEvent.setup();
  const items = ['Item 1', 'Item 2', 'Item 3'];
  render(<SortableList items={items} />);
  
  const firstItem = screen.getByText('Item 1');
  const thirdItem = screen.getByText('Item 3');
  
  // Drag first item to third position
  await user.dragAndDrop(firstItem, thirdItem);
  
  // Verify new order
  const listItems = screen.getAllByRole('listitem');
  expect(listItems[0]).toHaveTextContent('Item 2');
  expect(listItems[1]).toHaveTextContent('Item 3');
  expect(listItems[2]).toHaveTextContent('Item 1');
});</code></pre>
        
        <h2>Testing File Uploads</h2>
        <pre><code>test('uploads file successfully', async () => {
  const user = userEvent.setup();
  const file = new File(['hello'], 'hello.png', { type: 'image/png' });
  
  render(<FileUpload onUpload={jest.fn()} />);
  
  const input = screen.getByLabelText(/upload file/i);
  await user.upload(input, file);
  
  expect(input.files[0]).toBe(file);
  expect(input.files).toHaveLength(1);
  
  // Test multiple files
  const files = [
    new File(['file1'], 'file1.png', { type: 'image/png' }),
    new File(['file2'], 'file2.png', { type: 'image/png' })
  ];
  
  await user.upload(input, files);
  expect(input.files).toHaveLength(2);
});</code></pre>
      `,
      practice: {
        task: "Write comprehensive tests for a multi-step form with validation",
        starter: `// Test a registration form with steps: Personal Info → Account Details → Confirmation`,
        solution: `import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import '@testing-library/jest-dom';
import RegistrationForm from './RegistrationForm';

describe('RegistrationForm', () => {
  test('completes full registration flow', async () => {
    const user = userEvent.setup();
    const mockOnComplete = jest.fn();
    
    render(<RegistrationForm onComplete={mockOnComplete} />);
    
    // Step 1: Personal Information
    expect(screen.getByText('Personal Information')).toBeInTheDocument();
    
    await user.type(screen.getByLabelText(/first name/i), 'John');
    await user.type(screen.getByLabelText(/last name/i), 'Doe');
    await user.type(screen.getByLabelText(/email/i), 'john@example.com');
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    // Step 2: Account Details
    await waitFor(() => {
      expect(screen.getByText('Account Details')).toBeInTheDocument();
    });
    
    await user.type(screen.getByLabelText(/username/i), 'johndoe');
    await user.type(screen.getByLabelText(/password/i), 'SecurePass123!');
    await user.type(screen.getByLabelText(/confirm password/i), 'SecurePass123!');
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    // Step 3: Confirmation
    await waitFor(() => {
      expect(screen.getByText('Confirmation')).toBeInTheDocument();
    });
    
    expect(screen.getByText('John Doe')).toBeInTheDocument();
    expect(screen.getByText('john@example.com')).toBeInTheDocument();
    expect(screen.getByText('johndoe')).toBeInTheDocument();
    
    await user.click(screen.getByRole('button', { name: /complete registration/i }));
    
    await waitFor(() => {
      expect(mockOnComplete).toHaveBeenCalledWith({
        firstName: 'John',
        lastName: 'Doe',
        email: 'john@example.com',
        username: 'johndoe',
        password: 'SecurePass123!'
      });
    });
  });

  test('validates required fields on each step', async () => {
    const user = userEvent.setup();
    render(<RegistrationForm onComplete={jest.fn()} />);
    
    // Try to proceed without filling required fields
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    expect(screen.getByText(/first name is required/i)).toBeInTheDocument();
    expect(screen.getByText(/last name is required/i)).toBeInTheDocument();
    expect(screen.getByText(/email is required/i)).toBeInTheDocument();
  });

  test('validates password confirmation match', async () => {
    const user = userEvent.setup();
    render(<RegistrationForm onComplete={jest.fn()} />);
    
    // Complete step 1
    await user.type(screen.getByLabelText(/first name/i), 'John');
    await user.type(screen.getByLabelText(/last name/i), 'Doe');
    await user.type(screen.getByLabelText(/email/i), 'john@example.com');
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    // Step 2: Password mismatch
    await waitFor(() => {
      expect(screen.getByText('Account Details')).toBeInTheDocument();
    });
    
    await user.type(screen.getByLabelText(/username/i), 'johndoe');
    await user.type(screen.getByLabelText(/^password/i), 'password123');
    await user.type(screen.getByLabelText(/confirm password/i), 'differentpassword');
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    expect(screen.getByText(/passwords do not match/i)).toBeInTheDocument();
  });

  test('allows navigation back to previous steps', async () => {
    const user = userEvent.setup();
    render(<RegistrationForm onComplete={jest.fn()} />);
    
    // Complete step 1
    await user.type(screen.getByLabelText(/first name/i), 'John');
    await user.type(screen.getByLabelText(/last name/i), 'Doe');
    await user.type(screen.getByLabelText(/email/i), 'john@example.com');
    await user.click(screen.getByRole('button', { name: /next/i }));
    
    // Go to step 2
    await waitFor(() => {
      expect(screen.getByText('Account Details')).toBeInTheDocument();
    });
    
    // Go back to step 1
    await user.click(screen.getByRole('button', { name: /back/i }));
    
    await waitFor(() => {
      expect(screen.getByText('Personal Information')).toBeInTheDocument();
    });
    
    // Verify form data is preserved
    expect(screen.getByDisplayValue('John')).toBeInTheDocument();
    expect(screen.getByDisplayValue('Doe')).toBeInTheDocument();
    expect(screen.getByDisplayValue('john@example.com')).toBeInTheDocument();
  });
});`,
        hints: [
          "Test each step of the form separately",
          "Verify navigation between steps works correctly",
          "Test form validation on each step",
          "Ensure form data persists when navigating back"
        ]
      }
    },
    {
      id: "testing-3",
      title: "Testing Async Operations",
      description: "Learn to test asynchronous operations, API calls, and loading states",
      duration: "110 minutes",
      estimatedTime: 110,
      difficulty: 'intermediate',
      prerequisites: ['testing-2'],
      learningObjectives: [
        "Test async operations with waitFor",
        "Mock API calls and external services",
        "Test loading and error states",
        "Handle timers and delays in tests"
      ],
      content: `
        <h1>Testing Async Operations</h1>
        
        <h2>Testing API Calls with MSW</h2>
        <p>Mock Service Worker (MSW) provides the best API mocking experience:</p>
        
        <pre><code>// src/mocks/handlers.js
import { rest } from 'msw';

export const handlers = [
  rest.get('/api/users', (req, res, ctx) => {
    return res(
      ctx.json([
        { id: 1, name: 'John Doe', email: 'john@example.com' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com' }
      ])
    );
  }),
  
  rest.post('/api/users', (req, res, ctx) => {
    return res(
      ctx.status(201),
      ctx.json({ id: 3, ...req.body })
    );
  }),
  
  rest.get('/api/users/:id', (req, res, ctx) => {
    const { id } = req.params;
    return res(
      ctx.json({ id: Number(id), name: 'John Doe', email: 'john@example.com' })
    );
  })
];

// src/mocks/server.js
import { setupServer } from 'msw/node';
import { handlers } from './handlers';

export const server = setupServer(...handlers);

// src/setupTests.js
import { server } from './mocks/server';

beforeAll(() => server.listen());
afterEach(() => server.resetHandlers());
afterAll(() => server.close());</code></pre>
        
        <h2>Testing Components with API Calls</h2>
        <pre><code>import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import { rest } from 'msw';
import { server } from '../mocks/server';
import UserList from './UserList';

test('displays users from API', async () => {
  render(<UserList />);
  
  // Initially shows loading
  expect(screen.getByText(/loading/i)).toBeInTheDocument();
  
  // Wait for users to load
  await waitFor(() => {
    expect(screen.getByText('John Doe')).toBeInTheDocument();
    expect(screen.getByText('Jane Smith')).toBeInTheDocument();
  });
  
  // Loading should disappear
  expect(screen.queryByText(/loading/i)).not.toBeInTheDocument();
});

test('handles API error gracefully', async () => {
  // Override handler to return error
  server.use(
    rest.get('/api/users', (req, res, ctx) => {
      return res(ctx.status(500), ctx.json({ message: 'Server error' }));
    })
  );
  
  render(<UserList />);
  
  await waitFor(() => {
    expect(screen.getByText(/error loading users/i)).toBeInTheDocument();
  });
});

test('adds new user', async () => {
  const user = userEvent.setup();
  render(<UserList />);
  
  // Wait for initial load
  await waitFor(() => {
    expect(screen.getByText('John Doe')).toBeInTheDocument();
  });
  
  // Add new user
  await user.type(screen.getByLabelText(/name/i), 'New User');
  await user.type(screen.getByLabelText(/email/i), 'new@example.com');
  await user.click(screen.getByRole('button', { name: /add user/i }));
  
  // Verify new user appears
  await waitFor(() => {
    expect(screen.getByText('New User')).toBeInTheDocument();
  });
});</code></pre>
        
        <h2>Testing Custom Hooks with Async Operations</h2>
        <pre><code>import { renderHook, waitFor } from '@testing-library/react';
import { rest } from 'msw';
import { server } from '../mocks/server';
import { useFetch } from './useFetch';

test('useFetch returns data on success', async () => {
  const { result } = renderHook(() => useFetch('/api/users'));
  
  // Initially loading
  expect(result.current.loading).toBe(true);
  expect(result.current.data).toBe(null);
  expect(result.current.error).toBe(null);
  
  // Wait for fetch to complete
  await waitFor(() => {
    expect(result.current.loading).toBe(false);
  });
  
  expect(result.current.data).toEqual([
    { id: 1, name: 'John Doe', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com' }
  ]);
  expect(result.current.error).toBe(null);
});

test('useFetch handles errors', async () => {
  server.use(
    rest.get('/api/users', (req, res, ctx) => {
      return res(ctx.status(404), ctx.json({ message: 'Not found' }));
    })
  );
  
  const { result } = renderHook(() => useFetch('/api/users'));
  
  await waitFor(() => {
    expect(result.current.loading).toBe(false);
  });
  
  expect(result.current.data).toBe(null);
  expect(result.current.error).toBe('Not found');
});

test('useFetch refetch functionality', async () => {
  const { result } = renderHook(() => useFetch('/api/users'));
  
  await waitFor(() => {
    expect(result.current.loading).toBe(false);
  });
  
  // Trigger refetch
  act(() => {
    result.current.refetch();
  });
  
  expect(result.current.loading).toBe(true);
  
  await waitFor(() => {
    expect(result.current.loading).toBe(false);
  });
});</code></pre>
        
        <h2>Testing Timers and Delays</h2>
        <pre><code>import { act } from '@testing-library/react';

test('debounced search input', async () => {
  jest.useFakeTimers();
  const user = userEvent.setup({ delay: null });
  
  render(<SearchInput onSearch={mockSearch} />);
  
  const input = screen.getByRole('textbox');
  
  // Type quickly
  await user.type(input, 'react');
  
  // Search shouldn't be called yet
  expect(mockSearch).not.toHaveBeenCalled();
  
  // Fast-forward time
  act(() => {
    jest.advanceTimersByTime(500);
  });
  
  // Now search should be called
  expect(mockSearch).toHaveBeenCalledWith('react');
  
  jest.useRealTimers();
});

test('auto-save functionality', async () => {
  jest.useFakeTimers();
  const user = userEvent.setup({ delay: null });
  
  render(<AutoSaveForm />);
  
  await user.type(screen.getByRole('textbox'), 'content');
  
  // Fast-forward to trigger auto-save
  act(() => {
    jest.advanceTimersByTime(2000);
  });
  
  expect(screen.getByText(/saved/i)).toBeInTheDocument();
  
  jest.useRealTimers();
});</code></pre>
        
        <h2>Testing Race Conditions</h2>
        <pre><code>test('handles race conditions in search', async () => {
  const user = userEvent.setup();
  
  // Mock different response times
  server.use(
    rest.get('/api/search', (req, res, ctx) => {
      const query = req.url.searchParams.get('q');
      const delay = query === 'slow' ? 1000 : 100;
      
      return res(
        ctx.delay(delay),
        ctx.json([{ query, results: [\`Result for \${query}\`] }])
      );
    })
  );
  
  render(<SearchComponent />);
  
  const input = screen.getByRole('textbox');
  
  // Start slow search
  await user.type(input, 'slow');
  
  // Immediately start fast search
  await user.clear(input);
  await user.type(input, 'fast');
  
  // Fast results should appear, not slow ones
  await waitFor(() => {
    expect(screen.getByText('Result for fast')).toBeInTheDocument();
  });
  
  // Slow results should not appear
  await waitFor(() => {
    expect(screen.queryByText('Result for slow')).not.toBeInTheDocument();
  }, { timeout: 2000 });
});</code></pre>
      `,
      practice: {
        task: "Test a data dashboard component that fetches multiple API endpoints with loading and error states",
        starter: `// Test a dashboard that loads user stats, recent activities, and charts`,
        solution: `import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';
import { rest } from 'msw';
import { server } from '../mocks/server';
import Dashboard from './Dashboard';

// Mock chart library
jest.mock('recharts', () => ({
  BarChart: ({ children }) => <div data-testid="bar-chart">{children}</div>,
  Bar: () => <div />,
  XAxis: () => <div />,
  YAxis: () => <div />,
  CartesianGrid: () => <div />,
  Tooltip: () => <div />,
  ResponsiveContainer: ({ children }) => <div>{children}</div>
}));

describe('Dashboard', () => {
  test('loads all dashboard data successfully', async () => {
    render(<Dashboard />);
    
    // Should show loading states
    expect(screen.getByText(/loading stats/i)).toBeInTheDocument();
    expect(screen.getByText(/loading activities/i)).toBeInTheDocument();
    expect(screen.getByText(/loading chart/i)).toBeInTheDocument();
    
    // Wait for all data to load
    await waitFor(() => {
      expect(screen.getByText('Total Users: 150')).toBeInTheDocument();
      expect(screen.getByText('Recent Activities')).toBeInTheDocument();
      expect(screen.getByTestId('bar-chart')).toBeInTheDocument();
    });
    
    // Loading states should disappear
    expect(screen.queryByText(/loading/i)).not.toBeInTheDocument();
  });

  test('handles partial API failures gracefully', async () => {
    // Make stats API fail
    server.use(
      rest.get('/api/stats', (req, res, ctx) => {
        return res(ctx.status(500));
      })
    );
    
    render(<Dashboard />);
    
    await waitFor(() => {
      // Stats should show error
      expect(screen.getByText(/error loading stats/i)).toBeInTheDocument();
      
      // Other sections should still load
      expect(screen.getByText('Recent Activities')).toBeInTheDocument();
      expect(screen.getByTestId('bar-chart')).toBeInTheDocument();
    });
  });

  test('refreshes data when refresh button is clicked', async () => {
    const user = userEvent.setup();
    
    // Track API calls
    let statsCallCount = 0;
    server.use(
      rest.get('/api/stats', (req, res, ctx) => {
        statsCallCount++;
        return res(ctx.json({ totalUsers: 150, activeUsers: 120 }));
      })
    );
    
    render(<Dashboard />);
    
    // Wait for initial load
    await waitFor(() => {
      expect(screen.getByText('Total Users: 150')).toBeInTheDocument();
    });
    
    expect(statsCallCount).toBe(1);
    
    // Click refresh
    await user.click(screen.getByRole('button', { name: /refresh/i }));
    
    // Should show loading again
    expect(screen.getByText(/loading stats/i)).toBeInTheDocument();
    
    // Wait for refresh to complete
    await waitFor(() => {
      expect(screen.getByText('Total Users: 150')).toBeInTheDocument();
    });
    
    expect(statsCallCount).toBe(2);
  });

  test('filters activities by date range', async () => {
    const user = userEvent.setup();
    
    render(<Dashboard />);
    
    // Wait for initial load
    await waitFor(() => {
      expect(screen.getByText('Recent Activities')).toBeInTheDocument();
    });
    
    // Select date range
    await user.selectOptions(screen.getByLabelText(/date range/i), 'last-week');
    
    // Should show loading for activities
    expect(screen.getByText(/loading activities/i)).toBeInTheDocument();
    
    // Wait for filtered data
    await waitFor(() => {
      expect(screen.getByText('Activities (Last Week)')).toBeInTheDocument();
    });
  });

  test('handles slow network conditions', async () => {
    // Add delay to all requests
    server.use(
      rest.get('/api/*', (req, res, ctx) => {
        return res(ctx.delay(2000), ctx.json({}));
      })
    );
    
    render(<Dashboard />);
    
    // Should show loading for extended time
    expect(screen.getByText(/loading stats/i)).toBeInTheDocument();
    
    // Fast-forward time
    jest.useFakeTimers();
    act(() => {
      jest.advanceTimersByTime(1500);
    });
    
    // Should still be loading
    expect(screen.getByText(/loading stats/i)).toBeInTheDocument();
    
    act(() => {
      jest.advanceTimersByTime(1000);
    });
    
    // Now should be loaded
    await waitFor(() => {
      expect(screen.queryByText(/loading stats/i)).not.toBeInTheDocument();
    });
    
    jest.useRealTimers();
  });
});`,
        hints: [
          "Mock external libraries like chart components",
          "Test loading states and their transitions",
          "Test partial failures and error handling",
          "Test user interactions that trigger new API calls"
        ]
      }
    }
  ],
  requirements: [
    "Solid understanding of React fundamentals",
    "Familiarity with modern JavaScript testing concepts",
    "Basic knowledge of API interactions",
    "Understanding of asynchronous programming"
  ],
  tags: ["Testing", "React", "Jest", "React Testing Library", "Quality Assurance"],
  imageSrc: "/images/course-thumbnail.png",
  learningOutcomes: [
    "Write comprehensive tests for React components",
    "Test user interactions and form handling",
    "Handle asynchronous operations and API mocking",
    "Implement effective testing strategies",
    "Debug and troubleshoot test failures",
    "Set up and maintain testing environments"
  ],
  certificateTemplate: "react-testing-mastery"
};
